import foodsDb from '../data/sgFoodCalories.json' with { type: 'json' };

const WORD_QTY = {
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  double: 2,
  triple: 3
};

const PORTION_MUL = foodsDb.portion_multipliers || {
  small: 0.85,
  regular: 1,
  large: 1.18,
  xl: 1.32
};

const MEAL_TYPE_DEFAULTS = foodsDb.meal_type_defaults || {
  breakfast: { kcal: 430, kcal_min: 410, kcal_max: 450 },
  lunch: { kcal: 560, kcal_min: 540, kcal_max: 580 },
  dinner: { kcal: 620, kcal_min: 600, kcal_max: 640 },
  snack: { kcal: 190, kcal_min: 175, kcal_max: 205 }
};

const FOODS = Array.isArray(foodsDb.foods) ? foodsDb.foods : [];

export const SG_FOOD_NAMES = FOODS
  .filter((f) => f.category !== 'addon')
  .map((f) => f.name);

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export function normalizeMealText(text) {
  let s = String(text || '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[’']/g, '');
  // Keep letters/digits from any language so “鸡饭” / “nasi lemak” both remember.
  s = s.replace(/[^\p{L}\p{N}\s]/gu, ' ').replace(/\s+/g, ' ').trim();
  const replacements = [
    [/\bbee hoon\b/g, 'beehoon'],
    [/\bbee hoons\b/g, 'beehoon'],
    [/\bbihun\b/g, 'beehoon'],
    [/\bwanton\b/g, 'wonton'],
    [/\bwantan\b/g, 'wonton'],
    [/\bchar siu\b/g, 'char siew'],
    [/\bkuay teow\b/g, 'kway teow'],
    [/\bais kacang\b/g, 'ice kachang'],
    [/\bice kacang\b/g, 'ice kachang'],
    [/\bcendol\b/g, 'chendol'],
    [/\bchai tow kway\b/g, 'carrot cake'],
    [/\bchai tow kueh\b/g, 'carrot cake'],
    [/\byong tau fu\b/g, 'yong tau foo'],
    [/\bytf\b/g, 'yong tau foo'],
    [/\bbcm\b/g, 'bak chor mee'],
    [/\bcai png\b/g, 'economic rice'],
    [/\bchye peng\b/g, 'economic rice'],
    [/\bmixed veg rice\b/g, 'economic rice'],
    [/\bmixed rice\b/g, 'economic rice'],
    [/\bteh ice\b/g, 'teh peng'],
    [/\bkopi ice\b/g, 'kopi peng'],
    [/\biced teh\b/g, 'teh peng'],
    [/\biced kopi\b/g, 'kopi peng'],
    [/\bice teh\b/g, 'teh peng'],
    [/\bice kopi\b/g, 'kopi peng']
  ];
  replacements.forEach(([pat, rep]) => {
    s = s.replace(pat, rep);
  });
  return s;
}

function aliasIndex(foods) {
  const pairs = [];
  foods.forEach((food) => {
    const names = [food.name, ...(food.aliases || [])];
    const seen = new Set();
    names.forEach((raw) => {
      const alias = normalizeMealText(raw);
      if (alias && !seen.has(alias)) {
        seen.add(alias);
        pairs.push([alias, food]);
      }
    });
  });
  pairs.sort((a, b) => b[0].length - a[0].length);
  return pairs;
}

const ALIAS_INDEX = aliasIndex(FOODS);

function findAlias(text, alias) {
  const re = new RegExp(`(?:^|\\s)${escapeRe(alias)}(?:\\s|$)`);
  const m = text.match(re);
  if (!m || m.index == null) return -1;
  return text[m.index] === ' ' ? m.index + 1 : m.index;
}

function qtyBefore(prefix) {
  const chunk = prefix.trim();
  if (!chunk) return 1;
  const tail = chunk.slice(-28);
  const num = tail.match(/(?:^|\s)(\d+(?:\.\d+)?)\s*(x|pcs?|pieces?|sticks?|plates?|bowls?|servings?|pkts?|packets?|sets?|cups?)?\s*$/);
  if (num) {
    const n = Number(num[1]);
    if (n > 0 && n <= 20) return n;
  }
  const word = tail.match(/(?:^|\s)(one|two|three|four|five|double|triple)\s*$/);
  if (word) return WORD_QTY[word[1]] || 1;
  return 1;
}

function scaledDefault(mealType, portionMul) {
  const d = MEAL_TYPE_DEFAULTS[mealType] || MEAL_TYPE_DEFAULTS.lunch;
  return {
    calories: Math.max(40, Math.round(d.kcal * portionMul)),
    kcalMin: Math.max(30, Math.round(d.kcal_min * portionMul)),
    kcalMax: Math.max(40, Math.round(d.kcal_max * portionMul))
  };
}

/**
 * Local Singapore-food calorie estimate. No API calls.
 * Returns { calories, kcalMin, kcalMax, note, source, matched, matches }.
 */
export function estimateSgMealCalories(description, mealType = 'lunch', portion = 'regular') {
  const text = normalizeMealText(description);
  const portionMul = PORTION_MUL[(portion || 'regular').toLowerCase()] || 1;
  const type = (mealType || 'lunch').toLowerCase();

  if (!text) {
    const d = scaledDefault(type, portionMul);
    return {
      ...d,
      note: 'Empty description — meal-type default',
      source: 'sg-db',
      matched: false,
      matches: []
    };
  }

  let remaining = ` ${text} `;
  const matches = [];

  ALIAS_INDEX.forEach(([alias, food]) => {
    let idx = findAlias(remaining, alias);
    while (idx >= 0) {
      const prefix = remaining.slice(0, idx);
      const qty = qtyBefore(prefix);
      const defaultQty = Number(food.default_qty) || 1;
      let factor = defaultQty ? qty / defaultQty : qty;
      if (defaultQty > 1 && Math.abs(qty - defaultQty) < 0.01) factor = 1;
      matches.push({
        id: food.id,
        name: food.name,
        qty,
        kcal: food.kcal,
        kcalMin: food.kcal_min,
        kcalMax: food.kcal_max,
        factor,
        serving: food.serving
      });
      remaining = `${remaining.slice(0, idx)} ${remaining.slice(idx + alias.length)}`;
      remaining = remaining.replace(/\s+/g, ' ');
      idx = findAlias(remaining, alias);
    }
  });

  if (!matches.length) {
    const d = scaledDefault(type, portionMul);
    return {
      ...d,
      note: `No exact SG dish match — used ${type} default. Try a dish name e.g. chicken rice, nasi lemak, laksa.`,
      source: 'sg-db',
      matched: false,
      matches: []
    };
  }

  let total = 0;
  let lo = 0;
  let hi = 0;
  const bits = [];
  matches.forEach((m) => {
    total += m.kcal * m.factor;
    lo += m.kcalMin * m.factor;
    hi += m.kcalMax * m.factor;
    const label = m.factor !== 1 ? `${m.name} ×${m.factor}` : m.name;
    bits.push(`${label} (${Math.round(m.kcal * m.factor)})`);
  });

  const calories = Math.max(40, Math.round(total * portionMul));
  const kcalMin = Math.max(30, Math.round(lo * portionMul));
  const kcalMax = Math.max(40, Math.round(hi * portionMul));
  const portionNote = portionMul === 1 ? '' : ` · ${portion} portion`;

  return {
    calories,
    kcalMin,
    kcalMax,
    note: `${bits.join(' + ')} · ${kcalMin}–${kcalMax} kcal${portionNote}`,
    source: 'sg-db',
    matched: true,
    matches
  };
}

export default estimateSgMealCalories;
